<template>
  <ModalDialog
    ref="dialog"
    title="Stocktake Manual Scan"
    :showSubmit="false"
    icon="fa-scanner"
    :scrollable="true"
    @closed="onClosed">

    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3">
      <transition name="fade">
        <NotificationBar v-if="submitSuccessMessage" type="success">
          <NotificationBarTitle>{{submitSuccessMessage}}</NotificationBarTitle>
        </NotificationBar>
      </transition>
      <FormRow label="Barcode Scan">
        <FormInputText ref="barcodeEntry" v-model="entry.barcode" field="barcode" @enter="onBarcodeEntered" :readonly="submittingEntry" note="User a barcode scanner or manually enter the barcode & press enter" />
      </FormRow>
      <Spinner v-if="loadingData" />
      <div v-show="lookupData">
        <FormRow label="Glass Info" v-if="isGlassLevel && lookupData.data.stock && lookupData.data.glassSize">
          <div class="row my-1">
            <div class="col-3"><strong>Stock: </strong></div>
            <div class="col-9">{{lookupData.data.stock.code}} - {{lookupData.data.stock.name}}</div>
          </div>
          <div class="row my-1">
            <div class="col-3"><strong>Actual Size: </strong></div>
            <div class="col-9">{{lookupData.data.height}}x{{lookupData.data.width}}</div>
          </div>
          <div class="row my-1">
            <div class="col-3"><strong>Ordered Size: </strong></div>
            <div class="col-9">{{lookupData.data.glassSize.height}}x{{lookupData.data.glassSize.width}}</div>
          </div>
        </FormRow>
        <FormRow label="Stock Info" v-if="isGeneralStock">
          <div class="row my-1">
            <div class="col-3"><strong>Stock: </strong></div>
            <div class="col-9">{{lookupData.data.code}} - {{lookupData.data.name}}</div>
          </div>
        </FormRow>
        <FormHorizontalRule />
        
        <div v-show="isGlassLevel">
          <FormRow label="Loose Sheets">
            <FormInputText ref="sheetsEntry" type="number" v-model="entry.sheetQuantity" field="sheetQuantity" @enter="onSheetsEntered" :readonly="submittingEntry" />
          </FormRow>
          <FormRow label="Blocks">
            <FormInputText ref="blocksEntry" type="number" v-model="entry.blockQuantity" field="blockQuantity" @enter="onBlocksEntered" :readonly="submittingEntry" />
          </FormRow>
          <FormRowFull v-show="entry.displayBlockList">
            <FormHorizontalRule />
            <NotificationBar type="warning">
              <NotificationBarTitle>Block count is not correct or errors found in the list. Please confirm blocks found</NotificationBarTitle>
            </NotificationBar>
            <StaticDataTable class="static-table-list" variant="table-sm">
              <template v-slot:header>
                <tr>
                  <th>Barcode</th>
                  <th>Sheets</th>
                  <th>Supplier Batch Number</th>
                  <th>Exists</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="(block, index) in entry.blockList" :key="index">
                  <td>
                    <span :class="{'text-danger': block.isOpened}">{{block.barcode}}</span>
                    <div v-if="block.isOpened"><small>Block is currently opened. Selecting this will close it</small></div>
                  </td>
                  <td>{{block.sheetQuantity}}</td>
                  <td>{{block.referenceCode}}</td>
                  <td>
                    <FormCheckbox v-model="block.exists" @change="blockExistsUpdated" />
                  </td>
                </tr>
                <tr class="static-datatable-row-nostripe border-top font-weight-bold">
                  <td colspan="3"> </td>
                  <td>{{blocksSelectedCount}} / {{entry.blockList.length}}</td>
                </tr>
              </template>
            </StaticDataTable>
          </FormRowFull>
        </div>
        <div v-show="isGeneralStock">
          <FormRow label="Quantity">
            <FormInputText ref="generalQuantity" type="number" v-model="entry.stockQuantity" field="stockQuantity" @enter="onStockQuantityEntered" :readonly="submittingEntry" :addon="uomDisplayUnits(lookupData ? lookupData.data.uomType : '')" />
          </FormRow>
        </div>
        <FormRowFull>
          <FormButton class="btn-primary btn-lg btn-block" @click="onSubmitEntry" :loading="submittingEntry" loadingStyle="slide-right" >Update</FormButton>
        </FormRowFull>
      </div>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import Utils from '@/utils/utils'

import StaticDataTable from '@/components/table/staticDataTable.vue'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import { UOMTypeUnitsLookup, StocktakeItemTypes } from '@/store/types'

export default {
  name: 'StockTakeScanEntryDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    StaticDataTable
  },
  props: {
    stockTakeId: Number,
    stockTakeItemId: Number
  },
  data() {
    return {
      dialogForm: {},
      entry: {
        barcode: '',
        stockQuantity: 0,
        sheetQuantity: 0,
        blockQuantity: 0,
        blockList: [],
        displayBlockList: false
      },
      lookupData: null,
      loadingData: false,
      stockTakeItem: null,
      submittingEntry: false,
      submitSuccessMessage: ''
    }
  },
  mounted() {

    // If a stocktake item was specified, run a lookup on that
    if(this.$props.stockTakeItemId != null) {
      this.loadingData = true

      this.$apiService.inventoryService.getStockTakeItem(this.$props.stockTakeId, this.$props.stockTakeItemId)
      .then(response => {
        this.stockTakeItem = _.cloneDeep(response.data)

        var barcode = null
        if(response.data.type == StocktakeItemTypes.glassLevel || response.data.type == StocktakeItemTypes.glassOffcut)
          barcode = _.get(response.data, 'glassLevel.barcode')
        else if(response.data.type == StocktakeItemTypes.generalStock)
          barcode = _.get(response.data, 'stock.barcode')

        // Run barcode lookup
        if(barcode) {
          this.entry.barcode = barcode
          this.onBarcodeEntered()
        }
      })
      .catch(err => {
        this.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.loadingData = false
      })

    } else {

      this.$nextTick(() => {
        this.$refs.barcodeEntry.focus()
      })
    }
  },
  computed: {
    isGlassLevel() {
      if(this.lookupData == null)
        return false
      return this.lookupData.model == 'glassLevel'
    },
    isGeneralStock() {
      if(this.lookupData == null)
        return false
      return this.lookupData.model == 'stock'
    },
    blocksSelectedCount() {
      return this.entry.blockList.filter(block => block.exists).length
    }
  },
  methods: {
    show() {
      // Reset form
      this.dialogForm = { }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    resetForm() {
      this.lookupData = null
      this.stockTakeItem = null
      this.entry.barcode = ''
      this.$nextTick(() => {
        this.$refs.barcodeEntry.focus()
      })
    },
    onBarcodeEntered(ev) {
      if(ev)
        ev.preventDefault()
      
      var barcode = this.entry.barcode
      this.dialogForm = this.clearFormResponse()

      // Reset
      this.lookupData = null
      this.loadingData = true
      
      this.$apiService.inventoryService.runStockTakeStockBarcodeLookup(this.$props.stockTakeId, barcode)
      .then(response => {
        this.lookupData = response.data

        this.entry.stockQuantity = _.get(this.lookupData, 'data.stockTakeItem.quantity', 0)
        this.entry.sheetQuantity = _.get(this.lookupData, 'data.stockTakeItem.quantity', 0)
        this.entry.blockQuantity = _.get(this.lookupData, 'data.stockTakeItem.blockQuantity', 0)
        this.entry.blockList = []
        this.entry.displayBlockList = false

        if(this.lookupData.data && _.isArray(this.lookupData.data.glassBlocks)) {
          this.entry.blockList = this.lookupData.data.glassBlocks.map(block => {
            var exists = false

            return {
              id: block.id,
              sheetQuantity: block.sheetQuantity,
              barcode: block.barcode,
              referenceCode: block.referenceCode,
              exists: exists,
              isOpened: block.isOpened
            }
          })
        }

        // Add in opened blocks scanned in for reconciling
        if(this.stockTakeItem && _.isArray(this.stockTakeItem.stockTakeItemCounts)) {

          this.stockTakeItem.stockTakeItemCounts.forEach(countInfo => {
            if(countInfo.quantity > 0 && countInfo.isBlockItem && countInfo.glassBlock) {

              let blockEntry = this.entry.blockList.find(block => block.id == countInfo.glassBlock.id)

              // If in the list, flag as existing
              if(blockEntry)
                blockEntry.exists = true

              if(!blockEntry && countInfo.glassBlock.isOpened) {
                this.entry.blockList.push({
                  id: countInfo.glassBlock.id,
                  sheetQuantity: countInfo.glassBlock.sheetQuantity,
                  barcode: countInfo.glassBlock.barcode,
                  referenceCode: countInfo.glassBlock.referenceCode,
                  exists: true,
                  isOpened: countInfo.glassBlock.isOpened
                })
              }
            }
          })          
        }

        // If we've got counts & blocks, display the blocks straight away
        if(this.entry.stockQuantity + this.entry.sheetQuantity > 0 || this.entry.blockQuantity > 0 && this.stockTakeItem != null) {
          this.entry.displayBlockList = true
        }

        this.$nextTick(() => {
          if(this.isGlassLevel) {
            this.$refs.sheetsEntry.focus()
            this.$refs.sheetsEntry.select()
          }
          else if(this.isGeneralStock) {
            this.$refs.generalQuantity.focus()
            this.$refs.generalQuantity.select()
          }
        })
      })
      .catch(err => {
        // Display results
        this.dialogForm = this.processFormResponse(err)
        this.resetForm()
      })
      .finally(() => {
        this.loadingData = false
      })
    },
    onSheetsEntered(ev) {
      ev.preventDefault()

      this.$nextTick(() => {
        this.$refs.blocksEntry.focus()
        this.$refs.blocksEntry.select()
      })
    },
    onBlocksEntered(ev) {
      ev.preventDefault()
      this.onSubmitEntry()
    },
    onStockQuantityEntered(ev) {
      ev.preventDefault()
      this.onSubmitEntry()
    },

    onSubmitEntry() {
      this.dialogForm = this.clearFormResponse()
      this.submitSuccessMessage = ''

      var batchUpdateList = []

      if(this.lookupData.model == 'glassLevel') {
        let blockQuantity = parseInt(this.entry.blockQuantity)

        batchUpdateList.push({
          type: StocktakeItemTypes.glassLevel,
          quantity: parseInt(this.entry.sheetQuantity),
          stockId: this.lookupData.data.stockId,
          glassLevelId: this.lookupData.id,
          isManualOverride: true
        })

        // If block count doesn't match up (or there are errors), we need to display the list of blocks & let the user choose what blocks will be selected
        // If the user has already been displayed & selected blocks, continue
        let blockListHasOpenedBlocks = this.entry.blockList.find(block => block.isOpened)

        if((blockQuantity != this.entry.blockList.length || blockListHasOpenedBlocks) && !this.entry.displayBlockList) {
          this.entry.displayBlockList = true

          // Pre-populate the blocks selected up to the block quantity
          // Make sure we already select the blocks currently flagged
          var preFilledCount = blockQuantity - this.entry.blockList.filter(block => block.exists).length
          this.entry.blockList.forEach((block, index) => {
            if(preFilledCount > 0 && !block.exists) {
              block.exists = true
              preFilledCount--
            }
          })
          return
        }
        if(blockQuantity == this.entry.blockList.length) {
          // If block count is correct, default to all blocks selected
          this.entry.blockList.forEach(block => {
            block.exists = true
          })
        }

        this.entry.blockList.forEach(block => {
          batchUpdateList.push({
            type: StocktakeItemTypes.glassLevel,
            quantity: block.exists ? 1 : 0,
            isBlockItem: true,
            stockId: this.lookupData.data.stockId,
            glassLevelId: this.lookupData.id,
            glassBlockId: block.id,
            isManualOverride: true,
            closeOpenedBlock: block.exists && block.isOpened
          })
        })
      }

      if(this.lookupData.model == 'stock') {
        batchUpdateList.push({
          type: StocktakeItemTypes.generalStock,
          quantity: parseInt(this.entry.stockQuantity),
          stockId: this.lookupData.data.id,
          isManualOverride: true
        })
      }

      // Submit update
      this.submittingEntry = true
      this.$apiService.inventoryService.updateStockTakeItemCountBatch(this.$props.stockTakeId, { itemList: batchUpdateList })
      .then(response => {
        this.resetForm()
        this.submitSuccessMessage = 'Update submitted'

        setTimeout(() => {
          this.submitSuccessMessage = ''
        }, 2000)
      })
      .catch(err => {
        this.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.submittingEntry = false
      })
    },

    uomDisplayUnits(uomType) {
      return Utils.getLookupName(UOMTypeUnitsLookup, uomType)
    },

    blockExistsUpdated() {
      // Update the block count based on 'exists' flag changed on block list
      this.entry.blockQuantity = this.entry.blockList.filter(block => block.exists).length
    }
  }
}
</script>